import type { EmploymentType, OrganizationTeam, SkillGrade, UserResource } from "@/engine/types";
import { EMPLOYMENT_LABEL, GRADE_LABEL, TEAM_COLORS } from "./resource-meta";

export const ORG_EXCEL_SCHEMA = "org.v1";
export const WORKING_DAYS_PER_MONTH = 21;

export const ORG_SHEET = {
  guide: "안내",
  exampleTeam: "예시_팀",
  examplePeople: "예시_인력",
  codeEmployment: "코드_고용형태",
  codeGrade: "코드_등급",
  teams: "팀",
  people: "인력",
  assignments: "참고_투입",
} as const;

export const TEAM_HEADERS = ["id", "팀이름", "설명", "리더", "컬러"] as const;
export const PEOPLE_HEADERS = ["id", "이름", "이메일", "직책", "팀", "고용형태", "등급", "월단가", "스킬", "메모", "활성", "가동률"] as const;
export const ASSIGNMENT_HEADERS = ["플랜id", "플랜이름", "이름", "이메일", "역할", "시작", "종료", "투입률", "M/M"] as const;

export const TAB_COLOR = {
  guide: "FFD1D1D1",
  example: "FF7CB342",
  code: "FFF0C14D",
  data: "FF5B9BD5",
  reference: "FFBDBDBD",
} as const;

export const HEADER_FILL = "FFE8EAED";
export const READONLY_FILL = "FFF3F4F6";
export const EXAMPLE_FILL = "FFF1F8E9";

export type TeamHeader = (typeof TEAM_HEADERS)[number];
export type PeopleHeader = (typeof PEOPLE_HEADERS)[number];

const EMPLOYMENT_BY_LABEL = new Map<string, EmploymentType>(
  (Object.entries(EMPLOYMENT_LABEL) as Array<[EmploymentType, string]>).map(([code, label]) => [normKey(label), code]),
);
const GRADE_BY_LABEL = new Map<string, SkillGrade>(
  (Object.entries(GRADE_LABEL) as Array<[SkillGrade, string]>).map(([code, label]) => [normKey(label), code]),
);
const COLOR_BY_LABEL = new Map(TEAM_COLORS.map((c) => [normKey(c.label), c.value]));

export function normKey(value: string): string {
  return value.trim().toLowerCase();
}

export function parseEmployment(raw: string): EmploymentType | null {
  const key = normKey(raw);
  if (!key) return null;
  if (key in EMPLOYMENT_LABEL) return key as EmploymentType;
  return EMPLOYMENT_BY_LABEL.get(key) ?? null;
}

export function parseGrade(raw: string): SkillGrade | null {
  const key = normKey(raw);
  if (!key) return null;
  if (key in GRADE_LABEL) return key as SkillGrade;
  return GRADE_BY_LABEL.get(key) ?? null;
}

export function parseActive(raw: string): boolean | null {
  const key = normKey(raw);
  if (!key) return true;
  if (["y", "yes", "true", "1", "예", "활성", "사용"].includes(key)) return true;
  if (["n", "no", "false", "0", "아니오", "비활성", "중지"].includes(key)) return false;
  return null;
}

export function parseColor(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (/^#([0-9a-f]{6})$/i.test(trimmed)) return trimmed.toLowerCase();
  return COLOR_BY_LABEL.get(normKey(trimmed)) ?? null;
}

export function parseMoney(raw: string): number | null {
  const cleaned = raw.replace(/[,_\s원]/g, "").trim();
  if (!cleaned) return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

export function parseSkills(raw: string): string[] {
  return raw
    .split(/[,/|]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export function formatActive(active: boolean): string {
  return active === false ? "N" : "Y";
}

export function formatUtilization(ratio: number): number {
  return Math.round(ratio * 100);
}

export function employmentChoices(): string[] {
  return Object.values(EMPLOYMENT_LABEL);
}

export function gradeChoices(): string[] {
  return Object.values(GRADE_LABEL);
}

export type ParsedTeamRow = {
  excelRow: number;
  id: string;
  name: string;
  description: string;
  leader: string;
  color: string;
};

export type ParsedPersonRow = {
  excelRow: number;
  id: string;
  name: string;
  email: string;
  roleTitle: string;
  team: string;
  employment: string;
  grade: string;
  monthlyRate: string;
  skills: string;
  notes: string;
  active: string;
  utilization: string;
};

export type OrgExcelCatalog = {
  teams: Record<string, OrganizationTeam>;
  resources: Record<string, UserResource>;
};
